import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, X, Plus, Building2, User, Briefcase, Trash2, Linkedin, Mail, Phone, Check, Save } from 'lucide-react';
import RichTextEditor from '@/components/ui/rich-text-editor';

interface ProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: string;
  project?: {
    id: string;
    name: string;
    description?: string | null;
    tech_stack?: string[] | null;
    start_date?: string | null;
    end_date?: string | null;
    client_company_id?: string | null;
    client_manager_id?: string | null;
    experience_id?: string | null;
  } | null;
  onSaved: () => void;
}

interface Company {
  id: string;
  name: string;
}

interface CompanyPerson {
  id: string;
  name: string;
  designation?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
}

interface Experience {
  id: string;
  job_title: string;
  company_id?: string | null;
  companies?: { name: string } | null;
  start_date?: string | null;
  end_date?: string | null;
}

interface ManagerEntry {
  id?: string;
  isNew: boolean;
  isSaved: boolean;
  name: string;
  designation: string;
  email: string;
  phone: string;
  linkedin_url: string;
}

const ProjectForm = ({ open, onOpenChange, candidateId, project, onSaved }: ProjectFormProps) => {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [techStack, setTechStack] = useState<string[]>([]);
  const [techInput, setTechInput] = useState('');
  const [clientCompanyId, setClientCompanyId] = useState<string>('');
  const [experienceId, setExperienceId] = useState<string>('');
  
  // Separate managers for client company and experience company
  const [clientManagers, setClientManagers] = useState<ManagerEntry[]>([]);
  const [experienceManagers, setExperienceManagers] = useState<ManagerEntry[]>([]);
  
  // Existing managers from each company
  const [existingClientManagers, setExistingClientManagers] = useState<CompanyPerson[]>([]);
  const [existingExpManagers, setExistingExpManagers] = useState<CompanyPerson[]>([]);
  const [selectedClientManager, setSelectedClientManager] = useState<string>('');
  const [selectedExpManager, setSelectedExpManager] = useState<string>('');
  
  // New client creation
  const [newClientName, setNewClientName] = useState('');
  const [showNewClient, setShowNewClient] = useState(false);
  
  const { toast } = useToast();

  // Get client company info
  const getClientCompanyInfo = (): { id: string | null; name: string | null } => {
    if (showNewClient && newClientName.trim()) {
      return { id: null, name: newClientName.trim() };
    }
    if (clientCompanyId && clientCompanyId !== 'new') {
      const company = companies.find(c => c.id === clientCompanyId);
      return { id: clientCompanyId, name: company?.name || null };
    }
    return { id: null, name: null };
  };

  // Get experience company info
  const getExperienceCompanyInfo = (): { id: string | null; name: string | null } => {
    if (experienceId) {
      const selectedExp = experiences.find(e => e.id === experienceId);
      if (selectedExp?.company_id) {
        return { id: selectedExp.company_id, name: selectedExp.companies?.name || null };
      }
    }
    return { id: null, name: null };
  };

  const clientCompanyInfo = getClientCompanyInfo();
  const experienceCompanyInfo = getExperienceCompanyInfo();
  
  const showClientManagerSection = clientCompanyId || showNewClient;
  const showExperienceManagerSection = experienceId && experienceCompanyInfo.id;

  useEffect(() => {
    if (open) {
      fetchCompanies();
      fetchExperiences();
      if (project) {
        setName(project.name);
        setDescription(project.description || '');
        setStartDate(project.start_date || '');
        setEndDate(project.end_date || '');
        setTechStack(project.tech_stack || []);
        setClientCompanyId(project.client_company_id || '');
        setExperienceId(project.experience_id || '');
        // Load existing manager if set
        if (project.client_manager_id) {
          loadExistingProjectManager(project.client_manager_id, project.client_company_id);
        } else {
          setClientManagers([]);
          setExperienceManagers([]);
        }
      } else {
        resetForm();
      }
    }
  }, [open, project]);

  // Fetch existing managers for client company
  useEffect(() => {
    const info = getClientCompanyInfo();
    if (info.id) {
      fetchExistingManagers(info.id, 'client');
    } else {
      setExistingClientManagers([]);
    }
  }, [clientCompanyId, showNewClient, newClientName, companies]);

  // Fetch existing managers for experience company
  useEffect(() => {
    const info = getExperienceCompanyInfo();
    if (info.id) {
      fetchExistingManagers(info.id, 'experience');
    } else {
      setExistingExpManagers([]);
    }
  }, [experienceId, experiences]);

  const loadExistingProjectManager = async (managerId: string, companyId: string | null) => {
    try {
      const { data, error } = await supabase
        .from('company_people')
        .select('id, name, designation, email, phone, linkedin_url, company_id')
        .eq('id', managerId)
        .single();
      if (error) throw error;
      if (data) {
        const managerEntry = {
          id: data.id,
          isNew: false,
          isSaved: true,
          name: data.name,
          designation: data.designation || '',
          email: data.email || '',
          phone: data.phone || '',
          linkedin_url: data.linkedin_url || ''
        };
        // Determine if this manager belongs to client or experience company
        if (companyId && data.company_id === companyId) {
          setClientManagers([managerEntry]);
        } else {
          setExperienceManagers([managerEntry]);
        }
      }
    } catch (error) {
      console.error('Error loading existing manager:', error);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setTechStack([]);
    setTechInput('');
    setClientCompanyId('');
    setExperienceId('');
    setNewClientName('');
    setShowNewClient(false);
    setClientManagers([]);
    setExperienceManagers([]);
    setSelectedClientManager('');
    setSelectedExpManager('');
  };

  const fetchExperiences = async () => {
    try {
      const { data, error } = await supabase
        .from('candidate_experience')
        .select('id, job_title, start_date, end_date, company_id, companies:company_id (name)')
        .eq('candidate_id', candidateId)
        .order('start_date', { ascending: false });
      if (error) throw error;
      const transformed = (data || []).map(exp => ({
        id: exp.id,
        job_title: exp.job_title,
        start_date: exp.start_date,
        end_date: exp.end_date,
        company_id: exp.company_id,
        companies: Array.isArray(exp.companies) 
          ? (exp.companies[0] as { name: string } | undefined) || null
          : exp.companies as { name: string } | null
      }));
      setExperiences(transformed);
    } catch (error) {
      console.error('Error fetching experiences:', error);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .order('name');
      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchExistingManagers = async (companyId: string, type: 'client' | 'experience') => {
    try {
      const { data, error } = await supabase
        .from('company_people')
        .select('id, name, designation, email, phone, linkedin_url')
        .eq('company_id', companyId)
        .order('name');
      if (error) throw error;
      if (type === 'client') {
        setExistingClientManagers(data || []);
      } else {
        setExistingExpManagers(data || []);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const addTech = () => {
    const tech = techInput.trim();
    if (tech && !techStack.includes(tech)) {
      setTechStack([...techStack, tech]);
      setTechInput('');
    }
  };

  const removeTech = (tech: string) => {
    setTechStack(techStack.filter(t => t !== tech));
  };

  const handleTechKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTech();
    }
  };

  const addNewManager = (type: 'client' | 'experience') => {
    const newManager: ManagerEntry = {
      isNew: true,
      isSaved: false,
      name: '',
      designation: '',
      email: '',
      phone: '',
      linkedin_url: ''
    };
    if (type === 'client') {
      setClientManagers([...clientManagers, newManager]);
    } else {
      setExperienceManagers([...experienceManagers, newManager]);
    }
  };

  const addExistingManager = (type: 'client' | 'experience') => {
    const selectedId = type === 'client' ? selectedClientManager : selectedExpManager;
    const existingList = type === 'client' ? existingClientManagers : existingExpManagers;
    const currentList = type === 'client' ? clientManagers : experienceManagers;
    
    if (!selectedId) return;
    const existing = existingList.find(m => m.id === selectedId);
    if (!existing) return;
    
    if (currentList.some(m => m.id === existing.id)) {
      toast({ title: 'Manager already added', variant: 'destructive' });
      return;
    }

    const managerEntry: ManagerEntry = {
      id: existing.id,
      isNew: false,
      isSaved: true,
      name: existing.name,
      designation: existing.designation || '',
      email: existing.email || '',
      phone: existing.phone || '',
      linkedin_url: existing.linkedin_url || ''
    };

    if (type === 'client') {
      setClientManagers([...clientManagers, managerEntry]);
      setSelectedClientManager('');
    } else {
      setExperienceManagers([...experienceManagers, managerEntry]);
      setSelectedExpManager('');
    }
  };

  const updateManager = (type: 'client' | 'experience', index: number, field: keyof ManagerEntry, value: string) => {
    if (type === 'client') {
      const updated = [...clientManagers];
      (updated[index] as any)[field] = value;
      setClientManagers(updated);
    } else {
      const updated = [...experienceManagers];
      (updated[index] as any)[field] = value;
      setExperienceManagers(updated);
    }
  };

  const removeManager = (type: 'client' | 'experience', index: number) => {
    if (type === 'client') {
      setClientManagers(clientManagers.filter((_, i) => i !== index));
    } else {
      setExperienceManagers(experienceManagers.filter((_, i) => i !== index));
    }
  };

  const [savingManagerIndex, setSavingManagerIndex] = useState<{ type: 'client' | 'experience'; index: number } | null>(null);

  const saveManagerIndividually = async (type: 'client' | 'experience', index: number, companyId: string) => {
    const managers = type === 'client' ? clientManagers : experienceManagers;
    const manager = managers[index];
    
    if (!manager.name.trim()) {
      toast({ title: 'Manager name is required', variant: 'destructive' });
      return;
    }

    setSavingManagerIndex({ type, index });
    try {
      if (manager.isNew && !manager.id) {
        const { data: newManager, error } = await supabase
          .from('company_people')
          .insert({
            company_id: companyId,
            name: manager.name.trim(),
            designation: manager.designation.trim() || null,
            email: manager.email.trim() || null,
            phone: manager.phone.trim() || null,
            linkedin_url: manager.linkedin_url.trim() || null,
            role: 'client_contact'
          })
          .select('id')
          .single();
        
        if (error) throw error;
        
        // Update the manager with the saved id
        const updated = [...managers];
        updated[index] = { ...manager, id: newManager.id, isNew: false, isSaved: true };
        if (type === 'client') {
          setClientManagers(updated);
        } else {
          setExperienceManagers(updated);
        }
        toast({ title: 'Manager saved successfully' });
      } else if (manager.id) {
        const { error } = await supabase
          .from('company_people')
          .update({
            name: manager.name.trim(),
            designation: manager.designation.trim() || null,
            email: manager.email.trim() || null,
            phone: manager.phone.trim() || null,
            linkedin_url: manager.linkedin_url.trim() || null
          })
          .eq('id', manager.id);
        
        if (error) throw error;
        
        const updated = [...managers];
        updated[index] = { ...manager, isSaved: true };
        if (type === 'client') {
          setClientManagers(updated);
        } else {
          setExperienceManagers(updated);
        }
        toast({ title: 'Manager updated successfully' });
      }
    } catch (error: any) {
      toast({ title: 'Error saving manager', description: error.message, variant: 'destructive' });
    } finally {
      setSavingManagerIndex(null);
    }
  };

  const saveManagersToCompany = async (managers: ManagerEntry[], companyId: string): Promise<string[]> => {
    const savedIds: string[] = [];
    
    for (const manager of managers) {
      if (manager.isNew) {
        const { data: newManager, error } = await supabase
          .from('company_people')
          .insert({
            company_id: companyId,
            name: manager.name.trim(),
            designation: manager.designation.trim() || null,
            email: manager.email.trim() || null,
            phone: manager.phone.trim() || null,
            linkedin_url: manager.linkedin_url.trim() || null,
            role: 'client_contact'
          })
          .select('id')
          .single();
        
        if (error) throw error;
        savedIds.push(newManager.id);
      } else if (manager.id) {
        const { error } = await supabase
          .from('company_people')
          .update({
            designation: manager.designation.trim() || null,
            email: manager.email.trim() || null,
            phone: manager.phone.trim() || null,
            linkedin_url: manager.linkedin_url.trim() || null
          })
          .eq('id', manager.id);
        
        if (error) throw error;
        savedIds.push(manager.id);
      }
    }
    
    return savedIds;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: 'Project name is required', variant: 'destructive' });
      return;
    }

    // Validate all new managers have names
    const allManagers = [...clientManagers, ...experienceManagers];
    const invalidManager = allManagers.find(m => m.isNew && !m.name.trim());
    if (invalidManager) {
      toast({ title: 'Manager name is required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      let finalClientCompanyId: string | null = null;
      let finalClientManagerId: string | null = null;

      // Create new client company if needed
      if (showNewClient && newClientName.trim()) {
        const { data: newCompany, error: companyError } = await supabase
          .from('companies')
          .insert({
            name: newClientName.trim(),
            category: 'end_client'
          })
          .select('id')
          .single();
        
        if (companyError) throw companyError;
        finalClientCompanyId = newCompany.id;
      } else if (clientCompanyId && clientCompanyId !== 'new') {
        finalClientCompanyId = clientCompanyId;
      }

      // Save client managers
      if (finalClientCompanyId && clientManagers.length > 0) {
        const savedClientManagerIds = await saveManagersToCompany(clientManagers, finalClientCompanyId);
        if (savedClientManagerIds.length > 0) {
          finalClientManagerId = savedClientManagerIds[0]; // Primary manager
        }
      }

      // Save experience managers
      const expCompanyInfo = getExperienceCompanyInfo();
      if (expCompanyInfo.id && experienceManagers.length > 0) {
        await saveManagersToCompany(experienceManagers, expCompanyInfo.id);
      }

      const projectData = {
        candidate_id: candidateId,
        name: name.trim(),
        description: description.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
        tech_stack: techStack.length > 0 ? techStack : null,
        client_company_id: finalClientCompanyId,
        client_manager_id: finalClientManagerId,
        experience_id: experienceId || null
      };

      if (project?.id) {
        const { error } = await supabase
          .from('candidate_projects')
          .update(projectData)
          .eq('id', project.id);
        if (error) throw error;
        toast({ title: 'Project updated successfully' });
      } else {
        const { error } = await supabase
          .from('candidate_projects')
          .insert(projectData);
        if (error) throw error;
        toast({ title: 'Project added successfully' });
      }

      onSaved();
      onOpenChange(false);
    } catch (error: any) {
      toast({ title: 'Error saving project', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const renderManagerSection = (
    type: 'client' | 'experience',
    managers: ManagerEntry[],
    existingManagers: CompanyPerson[],
    selectedManager: string,
    setSelectedManager: (val: string) => void,
    companyName: string | null,
    companyId: string | null
  ) => (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4" />
          {type === 'client' ? 'Client Company' : 'Employer'} Managers
        </Label>
        <Badge variant="outline" className="text-xs">
          {companyName || 'New Company'}
        </Badge>
      </div>

      {/* Select existing manager */}
      {companyId && existingManagers.length > 0 && (
        <div className="flex gap-2">
          <Select value={selectedManager} onValueChange={setSelectedManager}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select existing contact..." />
            </SelectTrigger>
            <SelectContent>
              {existingManagers.filter(m => !managers.some(added => added.id === m.id)).map((manager) => (
                <SelectItem key={manager.id} value={manager.id}>
                  {manager.name} {manager.designation ? `(${manager.designation})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            type="button" 
            variant="secondary" 
            size="sm"
            onClick={() => addExistingManager(type)}
            disabled={!selectedManager}
          >
            Add
          </Button>
        </div>
      )}

      {/* Added managers list */}
      {managers.map((manager, index) => {
        const isSaving = savingManagerIndex?.type === type && savingManagerIndex?.index === index;
        return (
          <div key={index} className="p-3 bg-background rounded-lg border space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {manager.isNew && !manager.id ? 'New Manager' : 'Manager'} #{index + 1}
                </span>
                {manager.isSaved && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                    <Check className="h-3 w-3 mr-1" />
                    Saved
                  </Badge>
                )}
              </div>
              <div className="flex gap-1">
                {companyId && (
                  <Button 
                    type="button"
                    variant="secondary" 
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => saveManagerIndividually(type, index, companyId)}
                    disabled={isSaving || !manager.name.trim()}
                  >
                    {isSaving ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <Save className="h-3 w-3 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => removeManager(type, index)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={manager.name}
                onChange={(e) => updateManager(type, index, 'name', e.target.value)}
                placeholder="Name *"
              />
              <Input
                value={manager.designation}
                onChange={(e) => updateManager(type, index, 'designation', e.target.value)}
                placeholder="Designation"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="relative">
                <Mail className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={manager.email}
                  onChange={(e) => updateManager(type, index, 'email', e.target.value)}
                  placeholder="Email"
                  className="pl-8"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={manager.phone}
                  onChange={(e) => updateManager(type, index, 'phone', e.target.value)}
                  placeholder="Phone"
                  className="pl-8"
                />
              </div>
              <div className="relative">
                <Linkedin className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={manager.linkedin_url}
                  onChange={(e) => updateManager(type, index, 'linkedin_url', e.target.value)}
                  placeholder="LinkedIn URL"
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* Add new manager button */}
      <Button 
        type="button" 
        variant="outline" 
        size="sm"
        onClick={() => addNewManager(type)}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add New Manager
      </Button>

      <p className="text-xs text-muted-foreground">
        Managers will be saved to <span className="font-medium">{companyName || 'the new company'}</span>'s contacts
      </p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{project ? 'Edit Project' : 'Add Project'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter project name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe the project..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label>Tech Stack</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={techInput}
                onChange={(e) => setTechInput(e.target.value)}
                onKeyDown={handleTechKeyDown}
                placeholder="Add technology..."
              />
              <Button type="button" variant="secondary" onClick={addTech}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {techStack.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {techStack.map((tech, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {tech}
                    <X 
                      className="h-3 w-3 cursor-pointer hover:text-destructive" 
                      onClick={() => removeTech(tech)} 
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Link to Experience */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Link to Experience (Optional)
            </Label>
            <Select value={experienceId || 'none'} onValueChange={(val) => setExperienceId(val === 'none' ? '' : val)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select an experience entry..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific experience</SelectItem>
                {experiences.map((exp) => (
                  <SelectItem key={exp.id} value={exp.id}>
                    {exp.job_title} at {exp.companies?.name || 'Unknown'} ({exp.start_date || 'N/A'} - {exp.end_date || 'Present'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Optionally link this project to a specific job/experience entry
            </p>
          </div>

          {/* Client Company Selection */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Client Company
            </Label>
            {!showNewClient ? (
              <div className="space-y-2 mt-1">
                <Select value={clientCompanyId || 'none'} onValueChange={(val) => {
                  if (val === 'new') {
                    setShowNewClient(true);
                    setClientCompanyId('');
                  } else if (val === 'none') {
                    setClientCompanyId('');
                  } else {
                    setClientCompanyId(val);
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client company..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client company</SelectItem>
                    <SelectItem value="new">+ Add New Client</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2 mt-1">
                <div className="flex gap-2">
                  <Input
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="Enter new client name..."
                  />
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => {
                      setShowNewClient(false);
                      setNewClientName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This will create a new company in the database with category "End Client"
                </p>
              </div>
            )}
          </div>

          {/* Manager Sections */}
          {(showClientManagerSection || showExperienceManagerSection) && (
            <div className="border-t pt-4 space-y-4">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <User className="h-4 w-4" />
                Project Managers / Contacts
              </Label>

              {/* Client Company Managers */}
              {showClientManagerSection && renderManagerSection(
                'client',
                clientManagers,
                existingClientManagers,
                selectedClientManager,
                setSelectedClientManager,
                clientCompanyInfo.name,
                clientCompanyInfo.id
              )}

              {/* Experience Company Managers */}
              {showExperienceManagerSection && renderManagerSection(
                'experience',
                experienceManagers,
                existingExpManagers,
                selectedExpManager,
                setSelectedExpManager,
                experienceCompanyInfo.name,
                experienceCompanyInfo.id
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {project ? 'Update' : 'Add'} Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectForm;
